import React from "react";
import { motion } from "framer-motion";
import { Archive, Trash2, Pencil, Briefcase, Lock, Users as UsersIcon } from "lucide-react";
import { Avatar } from "../../components/primitives";
import { IconBtn } from "../../components/RowControls";
import { Shimmer } from "../../components/StatCard";
import { useDashTheme } from "../../hooks/useDashTheme";
import { isGeneralProject, fmtDate } from "./projectHelpers";
import {
  identityHue, monogramOf, statusMeta, priorityMeta, typeLabel,
  clientLabel, memberEntries, scheduleOf, scheduleColor, goalsOf,
} from "./projectVisuals";

/**
 * One project, as a card.
 *
 * The card leads with identity — a colour band carrying the project's own code
 * as a monogram — because the job of the grid is to be scanned, not read. Below
 * it, only what the record actually holds: description, type/client, goals,
 * schedule, team.
 *
 * Every absence is stated ("None set", "No dates set", "No members assigned")
 * rather than drawn as an empty bar or a zero, so a sparse project reads as
 * sparse instead of broken.
 */
export function ProjectCard({
  project: p, stat, statsLoading,
  canEdit, canManage,
  onOpen, onInfo, onEdit, onArchive, onDelete, onGoals, onMembers,
}) {
  const t = useDashTheme();
  const hue = identityHue(t, p);
  const st = statusMeta(p.status);
  const prio = priorityMeta(p.priority);
  const type = typeLabel(p);
  const client = clientLabel(p);
  const members = memberEntries(p);
  const sched = scheduleOf(p);
  const goals = goalsOf(stat);
  const general = isGeneralProject(p);

  // For raw <button>s inside the card, which would otherwise bubble up to the
  // card's own onClick and navigate away. NOT for IconBtn — that already calls
  // stopPropagation itself and invokes onClick with no event, so wrapping it
  // would dereference an undefined event and throw.
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <motion.div
      variants={t.stagger.item}
      className="tg-projcard tg-datarow"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(p)}
      onKeyDown={(e) => {
        // role="button" carries no implicit key handling.
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(p); }
      }}
      style={{
        display: "flex", flexDirection: "column", background: t.c.surface,
        border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
        boxShadow: t.shadow.sm, overflow: "hidden", cursor: "pointer",
        textAlign: "left", minWidth: 0,
      }}
    >
      {/* ── identity band ── */}
      <div style={{
        position: "relative", height: 74, padding: `${t.s[3]}px ${t.s[4]}px`,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: t.s[2], overflow: "hidden",
        background: `linear-gradient(135deg, ${hue}, ${hue}CC)`,
      }}>
        <span aria-hidden style={{
          position: "absolute", right: -14, bottom: -30, fontSize: 86, fontWeight: 700,
          letterSpacing: "-0.05em", lineHeight: 1, color: "#FFFFFF", opacity: 0.17,
          pointerEvents: "none", userSelect: "none",
        }}>{monogramOf(p)}</span>

        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, borderRadius: 9, flexShrink: 0,
          background: "rgba(255,255,255,0.22)", color: "#FFFFFF",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.28)",
          fontSize: t.font.size.base, fontWeight: 700, letterSpacing: "0.02em",
        }}>{monogramOf(p)}</span>

        <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", position: "relative" }}>
          <CoverChip dot>{st.label}</CoverChip>
          {prio && <CoverChip>{prio.label}</CoverChip>}
        </span>
      </div>

      {/* ── record ── */}
      <div style={{ padding: `${t.s[3]}px ${t.s[4]}px 0`, flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{
            fontSize: t.font.size.lg, fontWeight: 600, letterSpacing: "-0.012em", lineHeight: 1.3,
            color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{p.name}</span>
          {general && <Lock size={12} style={{ color: t.c.textTertiary, flexShrink: 0 }} aria-label="System project" />}
        </div>

        <p style={{
          fontSize: t.font.size.md, color: t.c.textTertiary, lineHeight: 1.5, margin: `5px 0 0`,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          overflow: "hidden", minHeight: 37,
        }}>{p.description || "No description"}</p>

        {(type || client) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: t.s[3] }}>
            {type && <Tag t={t}>{type}</Tag>}
            {client && <Tag t={t} icon={<Briefcase size={11} />}>{client}</Tag>}
          </div>
        )}

        {/* Goals — a two-segment meter, clickable through to the goals panel. */}
        <Meter
          t={t} label="Goals"
          right={goals ? `${goals.approved} of ${goals.total} approved` : null}
          loading={statsLoading && !stat}
          empty={goals ? null : "None set on this project"}
          onClick={goals ? () => onGoals(p) : null}
          title={goals ? `Goals on ${p.name}` : undefined}
        >
          {goals && (
            <div style={{ display: "flex", height: 6, borderRadius: 99, overflow: "hidden", background: t.c.surfaceMuted, gap: 1.5 }}>
              <span style={{ width: `${(goals.approved / goals.total) * 100}%`, background: t.status.positive }} />
              <span style={{ width: `${(goals.pending / goals.total) * 100}%`, background: t.status.warning, opacity: 0.5 }} />
            </div>
          )}
        </Meter>

        <ScheduleMeter t={t} sched={sched} />
      </div>

      {/* ── team + actions ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[2],
        marginTop: t.s[4], padding: `${t.s[3]}px ${t.s[4]}px`,
        borderTop: `1px solid ${t.c.border}`, background: t.c.surfaceMuted,
      }}>
        {members.length ? (
          <button
            onClick={stop(() => onMembers(p))}
            title={`Member hours on ${p.name}`}
            style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "transparent", cursor: "pointer", padding: 0, minWidth: 0 }}
          >
            {members.slice(0, 5).map((m, i) => (
              <span key={m.key} style={{ marginLeft: i ? -7 : 0, borderRadius: 99, display: "inline-flex", boxShadow: `0 0 0 2px ${t.c.surfaceMuted}` }}>
                <Avatar user={m.user} name={m.name} size={26} />
              </span>
            ))}
            {members.length > 5 && (
              <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginLeft: 4 }}>+{members.length - 5}</span>
            )}
          </button>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.md, color: t.c.textTertiary }}>
            <UsersIcon size={13} /> No members assigned
          </span>
        )}

        <span className="tg-row-actions" style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <IconBtn title="Quick view" onClick={() => onInfo(p)}><Briefcase size={15} /></IconBtn>
          {canEdit && <IconBtn title="Edit" onClick={() => onEdit(p)}><Pencil size={15} /></IconBtn>}
          {canManage && !general && (
            <IconBtn title={p.status === "archived" ? "Unarchive" : "Archive"} onClick={() => onArchive(p)}><Archive size={15} /></IconBtn>
          )}
          {canManage && !general && (
            <IconBtn title="Delete" danger onClick={() => onDelete(p)}><Trash2 size={15} /></IconBtn>
          )}
        </span>
      </div>
    </motion.div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

// Chips sitting on the identity band. White-on-colour, so they read on every
// hue in the series without needing a per-hue foreground.
function CoverChip({ children, dot }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
      background: "rgba(255,255,255,0.24)", color: "#FFFFFF",
    }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: "#FFFFFF" }} />}
      {children}
    </span>
  );
}

function Tag({ t, children, icon }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: t.r.sm,
      fontSize: t.font.size.xs, fontWeight: 500, background: t.c.surfaceMuted, color: t.c.textTertiary,
      border: `1px solid ${t.c.border}`, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>{icon}{children}</span>
  );
}

// Goals and Schedule share this frame so they read as one family rather than
// two unrelated widgets stacked on top of each other.
function Meter({ t, label, right, rightColor, children, empty, loading, onClick, title }) {
  const head = (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: t.s[2], marginBottom: 6 }}>
      <span style={{ fontSize: t.font.size.xs, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: t.c.textTertiary }}>{label}</span>
      {right && <span style={{ fontSize: t.font.size.sm, fontWeight: 500, color: rightColor || t.c.textSecondary }}>{right}</span>}
    </div>
  );

  const body = loading
    ? <Shimmer w="100%" h={6} />
    : empty
      ? <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, padding: "3px 0" }}>{empty}</div>
      : children;

  if (!onClick) return <div style={{ marginTop: t.s[3] }}>{head}{body}</div>;

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      style={{ marginTop: t.s[3], display: "block", width: "100%", border: "none", background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }}
    >
      {head}{body}
    </button>
  );
}

// The schedule bar carries a today marker, so it reads as a position inside a
// date range rather than as a percentage of work completed.
function ScheduleMeter({ t, sched }) {
  if (sched.kind === "system" || sched.kind === "none") {
    return <Meter t={t} label="Schedule" right={sched.right} empty={sched.note} />;
  }
  const color = scheduleColor(t, sched);
  const width = sched.closed ? 100 : sched.pct;
  return (
    <Meter t={t} label="Schedule" right={sched.right} rightColor={color}>
      <div style={{ position: "relative", height: 6, borderRadius: 99, background: t.c.surfaceMuted }}>
        <span style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: `${width}%`,
          borderRadius: 99, background: color, opacity: sched.closed ? 0.4 : 1,
        }} />
        {!sched.closed && (
          <span style={{
            position: "absolute", top: -3, left: `calc(${sched.pct}% - 1px)`,
            width: 2, height: 12, borderRadius: 99, background: t.c.text,
          }} />
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 6 }}>
        <span>{fmtDate(sched.start)}</span><span>{fmtDate(sched.end)}</span>
      </div>
    </Meter>
  );
}
